import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { CheckCircle, MapPin, ShoppingBag, Mail, FileText, Plus, Calendar, Save, X, AlertTriangle, Phone, ArrowLeft, ShoppingCart } from 'lucide-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useCartStore } from '../store/cartStore'
import { useAuth } from '../context/AuthContext'
import { orderService, addressService, templateService, authService } from '../services/api'
import { useNotificationStore } from '../store/notificationStore'
import { PageSpinner } from '../components/ui'
import toast from 'react-hot-toast'

function fmtPrice(p) {
  return `R ${parseFloat(p || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

// Min date = today, max = 90 days out
function minDate() {
  const d = new Date()
  return d.toISOString().split('T')[0]
}
function maxDate() {
  const d = new Date(); d.setDate(d.getDate() + 90)
  return d.toISOString().split('T')[0]
}

export default function Checkout() {
  const [submitting, setSubmitting]      = useState(false)
  const [orderDone, setOrderDone]        = useState(null)
  const [selectedAddressId, setSelected] = useState(null)
  const [notes, setNotes]                = useState('')
  const [deliveryDate, setDeliveryDate]  = useState('')

  // Save-as-template state
  const [tmplOpen, setTmplOpen]   = useState(false)
  const [tmplName, setTmplName]   = useState('')

  const { items, getTotal, clearCart } = useCartStore()
  const { isAuthenticated }            = useAuth()

  const { data: addresses, isLoading: loadingAddresses } = useQuery({
    queryKey: ['addresses'],
    queryFn:  addressService.getAll,
    enabled:  isAuthenticated,
    onSuccess: (data) => {
      const list = Array.isArray(data) ? data : []
      if (list.length > 0 && !selectedAddressId) {
        // Prefer default address, fall back to first
        const def = list.find(a => a.is_default) || list[0]
        setSelected(def.id)
      }
    },
  })

  const saveTmplMut = useMutation({
    mutationFn: () => templateService.create({
      name: tmplName.trim(),
      items: items.map(i => ({
        stock_code: i.product?.stock_code || '',
        quantity:   i.quantity,
        note:       null,
      })).filter(i => i.stock_code),
    }),
    onSuccess: () => { setTmplOpen(false); setTmplName(''); toast.success('Saved as template') },
    onError:   () => toast.error('Failed to save template'),
  })

  const subtotal = getTotal()
  const addrList = Array.isArray(addresses) ? addresses : []

  // Load customer profile to check can_order flag
  const { data: customerProfile } = useQuery({
    queryKey: ['customer', 'me'],
    queryFn: authService.customerMe,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })

  if (!items.length && !orderDone) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center animate-fade-in">
        <div className="text-6xl mb-4">🛒</div>
        <h2 className="font-heading text-2xl mb-3">Your cart is empty</h2>
        <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
          <ShoppingBag size={16} /> Start Shopping
        </Link>
      </div>
    )
  }

  // Block checkout if account is on hold
  if (customerProfile && customerProfile.can_order === false) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle size={36} className="text-amber-400" />
        </div>
        <h2 className="font-heading text-2xl text-gray-900 dark:text-white mb-3">Account On Hold</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-2">
          Your account currently has an outstanding balance, so new orders cannot be placed at this time.
        </p>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-8">
          Please settle your account and contact us to have ordering re-enabled.
        </p>
        <div className="card p-5 text-left mb-8 space-y-3 max-w-sm mx-auto">
          <h3 className="font-display font-semibold text-gray-800 dark:text-white mb-1 text-sm">Contact Us</h3>
          <a
            href="tel:+27123231520"
            className="flex items-center gap-3 text-sm text-primary hover:text-primary-dark transition-colors"
          >
            <Phone size={16}/> 012 323 1520
          </a>
          <a
            href="mailto:orders@tonysliquor.co.za"
            className="flex items-center gap-3 text-sm text-primary hover:text-primary-dark transition-colors"
          >
            <Mail size={16}/> orders@tonysliquor.co.za
          </a>
        </div>
        <Link to="/shop" className="btn-outline inline-flex items-center gap-2">
          <ShoppingBag size={16}/> Continue Browsing
        </Link>
      </div>
    )
  }

  // ── Order confirmed ──────────────────────────────────────────────
  if (orderDone) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-primary" />
        </div>
        <h2 className="font-heading text-3xl text-gray-900 dark:text-white mb-3">Order Placed!</h2>
        <p className="text-sm font-mono bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-lg inline-block mb-6">
          Order #{orderDone}
        </p>
        <div className="card p-5 text-left mb-8 space-y-3">
          <h3 className="font-display font-semibold text-gray-800 dark:text-white mb-1">What happens next</h3>
          <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
            <Mail size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span>A confirmation email has been sent to you with your order summary.</span>
          </div>
          <div className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-300">
            <FileText size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Your order has been sent to our team and will be processed shortly.</span>
          </div>
        </div>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/orders" className="btn-primary">View My Orders</Link>
          <Link to="/shop" className="btn-outline">Continue Shopping</Link>
        </div>
      </div>
    )
  }

  const handlePlaceOrder = async () => {
    if (!selectedAddressId && addrList.length > 0) {
      toast.error('Please select a delivery address')
      return
    }
    setSubmitting(true)
    try {
      const res = await orderService.placeOrder({
        address_id:     selectedAddressId || null,
        delivery_notes: notes,
        delivery_date:  deliveryDate || null,
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      })
      clearCart()
      const orderId = res?.order_number || res?.id || '—'
      setOrderDone(orderId)
      useNotificationStore.getState().add({
        type:    'order',
        title:   'Order Placed! 🎉',
        message: `Order #${orderId} confirmed. We'll be in touch soon.`,
        link:    '/orders',
      })
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.detail || 'Failed to place order')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
      {/* Nav row */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/cart')}
          className="inline-flex items-center gap-1.5 text-sm font-display font-semibold bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-xl shadow-sm transition-all hover:shadow-md active:scale-95 group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          Back
        </button>
        <h1 className="section-title mb-0">Place Order</h1>
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 text-sm font-display font-semibold text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 px-4 py-2 rounded-xl transition-colors"
        >
          <ShoppingCart size={14} />
          Continue Shopping
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">

        {/* ── Left column ─────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Delivery address */}
          <div className="card p-5">
            <h2 className="font-display font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <MapPin size={16} className="text-primary" /> Delivery Address
            </h2>
            {loadingAddresses ? <PageSpinner /> : addrList.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No saved addresses on your account.</p>
                <Link to="/account/addresses" className="btn-outline inline-flex items-center gap-2 text-sm">
                  <Plus size={14} /> Add an Address
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {addrList.map(addr => (
                  <button
                    key={addr.id}
                    onClick={() => setSelected(addr.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                      selectedAddressId === addr.id
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 transition-colors ${
                        selectedAddressId === addr.id ? 'border-primary bg-primary' : 'border-gray-300'
                      }`} />
                      <div>
                        {addr.address_type && (
                          <span className="text-xs font-display font-semibold text-primary uppercase tracking-wide">
                            {addr.address_type}
                          </span>
                        )}
                        <p className="text-sm text-gray-800 dark:text-white mt-0.5">
                          {addr.address_line1}{addr.address_line2 && `, ${addr.address_line2}`}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {[addr.city, addr.postal_code, addr.country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
                <Link to="/account/addresses" className="flex items-center gap-1.5 text-xs text-primary hover:underline mt-1 opacity-0 pointer-events-none hidden">
                  hidden
                </Link>
                {selectedAddressId && (() => {
                  const sel = addrList.find(a => a.id === selectedAddressId)
                  return sel ? (
                    <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/40 rounded-xl text-green-700 dark:text-green-400 text-xs">
                      <CheckCircle size={13} className="flex-shrink-0"/>
                      <span>Delivering to <strong>{sel.address_line1}{sel.city ? `, ${sel.city}` : ''}</strong></span>
                    </div>
                  ) : null
                })()}
              </div>
            )}
          </div>

          {/* Delivery date */}
          <div className="card p-5">
            <h2 className="font-display font-semibold text-gray-800 dark:text-white mb-1 flex items-center gap-2">
              <Calendar size={16} className="text-primary" /> Requested Delivery Date
              <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              Let us know when you'd like the order delivered. Subject to availability.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="date"
                className="input-field text-sm max-w-xs"
                value={deliveryDate}
                min={minDate()}
                max={maxDate()}
                onChange={e => setDeliveryDate(e.target.value)}
              />
              {deliveryDate && (
                <button
                  onClick={() => setDeliveryDate('')}
                  className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition-colors"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Order notes */}
          <div className="card p-5">
            <label className="block font-display font-semibold text-sm text-gray-800 dark:text-white mb-2">
              Order Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className="input-field resize-none text-sm"
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Special instructions, delivery windows, references…"
            />
          </div>

          {/* Save as template */}
          <div className="card p-5">
            <h2 className="font-display font-semibold text-gray-800 dark:text-white mb-1 flex items-center gap-2">
              <FileText size={16} className="text-primary" /> Save as Template
              <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
            </h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              Save this cart as a reusable template to re-order quickly next time.
            </p>
            {!tmplOpen ? (
              <button
                onClick={() => setTmplOpen(true)}
                className="flex items-center gap-2 text-sm text-primary border border-primary/30 rounded-xl px-4 py-2.5 hover:bg-primary/5 transition-colors font-display font-medium"
              >
                <Save size={14} /> Save Cart as Template
              </button>
            ) : (
              <div className="flex gap-2 items-center">
                <input
                  className="input-field text-sm flex-1"
                  value={tmplName}
                  onChange={e => setTmplName(e.target.value)}
                  placeholder="Template name, e.g. Weekly Beer Order…"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && tmplName.trim() && saveTmplMut.mutate()}
                />
                <button
                  onClick={() => saveTmplMut.mutate()}
                  disabled={!tmplName.trim() || saveTmplMut.isLoading}
                  className="btn-primary py-2 px-3 text-sm flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Save size={13} /> {saveTmplMut.isLoading ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => { setTmplOpen(false); setTmplName('') }} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
                  <X size={16} />
                </button>
              </div>
            )}
            {saveTmplMut.isSuccess && (
              <p className="text-xs text-green-500 mt-2 flex items-center gap-1">
                <CheckCircle size={12} /> Saved! View it in <Link to="/templates" className="underline">My Templates</Link>.
              </p>
            )}
          </div>

          {/* Info box */}
          <div className="flex items-start gap-3 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl p-4 text-sm text-gray-600 dark:text-gray-300">
            <FileText size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p>
              When you place your order, a confirmation email is sent to you. No payment is taken online; invoicing is handled separately.
            </p>
          </div>
        </div>

        {/* ── Right: order summary ─────────────────────────────────── */}
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="font-display font-semibold text-gray-800 dark:text-white mb-4">Order Summary</h3>

            <div className="space-y-2.5 max-h-72 overflow-y-auto scrollbar-thin mb-4">
              {items.map(item => {
                const name  = item.product?.description || item.product?.product_name || 'Product'
                const price = item.price || item.product?.inclprice || item.product?.inclprice2 || 0
                return (
                  <div key={item.id} className="flex justify-between text-sm gap-3">
                    <span className="text-gray-600 dark:text-gray-300 line-clamp-2 flex-1">
                      {name} <span className="text-gray-400">×{item.quantity}</span>
                    </span>
                    <span className="font-medium text-gray-800 dark:text-white flex-shrink-0">
                      {fmtPrice(price * item.quantity)}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Delivery date badge in summary if set */}
            {deliveryDate && (
              <div className="flex items-center gap-2 text-xs text-primary bg-primary/5 dark:bg-primary/10 rounded-lg px-3 py-2 mb-3">
                <Calendar size={12} />
                Deliver by: {new Date(deliveryDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}

            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-2 text-sm">
              <div className="flex justify-between text-gray-500 dark:text-gray-400">
                <span>{items.length} item{items.length !== 1 ? 's' : ''}</span>
                <span>{fmtPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-400 text-xs">
                <span>Delivery</span>
                <span>Invoiced separately</span>
              </div>
              <div className="flex justify-between font-bold text-gray-900 dark:text-white text-base pt-1 border-t border-gray-100 dark:border-gray-700">
                <span>Order Total</span>
                <span className="text-primary">{fmtPrice(subtotal)}</span>
              </div>
              <p className="text-xs text-gray-400">Prices include VAT</p>
            </div>

            <div className="mt-4 space-y-2">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
                ⚠️ By placing this order you confirm you are 18+ years of age.
              </div>
              <button
                onClick={handlePlaceOrder}
                disabled={submitting || addrList.length === 0}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? 'Placing Order…' : '✓ Place Order'}
              </button>
              {addrList.length === 0 && !loadingAddresses && (
                <p className="text-xs text-center text-amber-600 dark:text-amber-400">
                  Add a delivery address to your account first.
                </p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
